import { Application } from '@platform/client';
import { F5ApplicationParameters } from '@platform/client/schemas';
import { ErrorBox, FormsCheckBox, FormsInputDropdown } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Aanu-Gopald.svg';

type FormValues = {
  commit: boolean;
  referralMethod: string;
};

const F5_REFERRAL_OPTIONS = [
  { text: 'Fellow F5er', textValue: 'Fellow F5er' },
  { text: 'Your manager', textValue: 'Your manager' },
  { text: 'F5 Global Good', textValue: 'F5 Global Good' },
  { text: 'Radar/Sharepoint/Monday Minute', textValue: 'Radar/Sharepoint/Monday Minute' },
  { text: 'Francois Locoh-Donou message', textValue: 'Francois Locoh-Donou message' },
  { text: 'Other', textValue: 'Other' },
];

export const F5Submission = (props: ApplicationPageProps) => {
  const { onBack, progress, dataKey } = { ...props };
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);

  const { getApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<F5ApplicationParameters>(dataKey);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      commit: getApplicationDataByKey('commit') || false,
      referralMethod: getApplicationDataByKey('referralMethod') || '',
    },
  });

  const submitF5Mutation = Application.useSubmitF5Application<F5ApplicationParameters>();
  const queryClient = useQueryClient();

  const submit: SubmitHandler<FormValues> = (data) => {
    if (data.commit && data.referralMethod) {
      submitF5Mutation.mutate(
        {
          data: {
            ...getApplicationData(),
            referralMethod: data.referralMethod,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: Application.getGetProfessionalApplicationStatusQueryKey(),
            });
          },
          onError: () => {
            setFailed(true);
          },
        },
      );
    } else {
      setSubmitted(true);
    }
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Ready to submit? Just one more thing.'
        description='After you submit, your responses are final, so take a moment to make sure everything is just right.'
      >
        <FormsInputDropdown
          control={control}
          name='referralMethod'
          label='How did you find out about this opportunity?'
          placeholder='Select one'
          items={F5_REFERRAL_OPTIONS}
          type='default'
          error={errors.referralMethod}
          inputDisabled={true}
        />
        <FormsCheckBox
          control={control}
          name='commit'
          size='md'
          description={
            <div>
              If accepted, I commit to volunteering up to 80 hours during my assigned Wave and will
              coordinate timing with my manager, who will be notified upon acceptance.
            </div>
          }
        />
        {submitted && (
          <ErrorBox showAlertTriangle={true}>
            Agree to the program requirements checkbox to continue
          </ErrorBox>
        )}
        {failed && (
          <ErrorBox showAlertTriangle={true}>
            There was an error submitting your application. Please try again later.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
